import type { ReactNode } from "react";
import { opisZaMaloDanych } from "@shared/ocenaSlowna.js";
import { odmien } from "../../lib/polski.js";
import {
  powodZaMaloDanychParametru,
  stopkaOceny,
  type ParametrUzytkownika,
  type WidokOcenyUzytkownika,
} from "../../lib/wynikUzytkownika.js";
import { Badge } from "../ui/Badge.js";
import { Card } from "../ui/Card.js";
import { PlakietkaOceny } from "./PlakietkaOceny.js";

/**
 * Karta "Twoja ocena": ocena ogolna slowem, najmocniejszy obszar i obszar z najwieksza liczba uwag do poprawy (tylko gdy
 * da sie je uczciwie wskazac), cztery parametry z licznikami i ocena slowna kazdego, a pod nimi krotka stopka o regule
 * roboczej.
 *
 * Nie ma tu zadnej liczby poza licznikami wskazan (ile mocnych stron, ile do poprawy): ocena to slowo albo "Za mało
 * danych". Licznik niesie kropka w kolorze oceny (zlota przy mocnych stronach, szara przy rzeczach do poprawy), ale
 * znaczenie niesie tekst obok (ST-3).
 */

function LicznikParametru({ parametr }: { parametr: ParametrUzytkownika }): React.JSX.Element {
  if (parametr.wynik === null) {
    return (
      <p className="param__licznik">
        <span>{parametr.brak === "w-toku" ? "Jeszcze to oceniamy" : "Nie udało się tego ocenić"}</span>
      </p>
    );
  }
  return (
    <p className="param__licznik">
      <span>
        <i className="param__kropka" aria-hidden="true" />
        {`${parametr.mocne} ${odmien(parametr.mocne, "mocna", "mocne", "mocnych")}`}
      </span>
      <span>
        <i className="param__kropka param__kropka--poprawy" aria-hidden="true" />
        {`${parametr.doPoprawy} do poprawy`}
      </span>
      {/* Powod "Za mało danych" stoi w wierszu, a nie w dymku: dymka nie da sie wywolac klawiaturą ani dotykiem. */}
      {parametr.wynik.rodzaj === "za-malo-danych" && <span>{powodZaMaloDanychParametru(parametr.wynik)}</span>}
    </p>
  );
}

function OcenaParametru({ parametr }: { parametr: ParametrUzytkownika }): React.JSX.Element {
  if (parametr.wynik !== null) return <PlakietkaOceny wynik={parametr.wynik} />;
  return <Badge>{parametr.brak === "w-toku" ? "Oceniamy" : "Nie oceniono"}</Badge>;
}

export function KartaOceny({
  widok,
  uwagi,
  przelacznik,
}: {
  widok: WidokOcenyUzytkownika;
  /** Zdania o niepelnej ocenie (lib/wynikUzytkownika.ts, uwagiDoOceny). */
  uwagi: readonly string[];
  /** "Wynik dla: ..." - stoi pod tytulem, gdy mozna wybrac innego mowce. */
  przelacznik?: ReactNode;
}): React.JSX.Element {
  const { ogolna, najmocniejszy, najwiecejDoPoprawy } = widok;

  return (
    <Card tytul="Twoja ocena">
      {przelacznik}

      <div className="wynik-podsumowanie">
        <PlakietkaOceny wynik={ogolna} duza />
        <div className="wynik-podsumowanie__tekst">
          {ogolna.rodzaj === "za-malo-danych" && <p>{opisZaMaloDanych(ogolna)}</p>}
          {najmocniejszy && (
            <p>
              <span className="wynik-etykieta">Najmocniejszy obszar:</span> {najmocniejszy.tytul}.
            </p>
          )}
          {najwiecejDoPoprawy && (
            <p>
              <span className="wynik-etykieta">Najwięcej do poprawy:</span>{" "}
              {`${najwiecejDoPoprawy.tytul} (${najwiecejDoPoprawy.doPoprawy} ${odmien(najwiecejDoPoprawy.doPoprawy, "uwaga", "uwagi", "uwag")})`}.
            </p>
          )}
        </div>
      </div>

      <ul className="param-lista" aria-label="Ocena w poszczególnych obszarach">
        {widok.parametry.map((p) => (
          <li className="param" key={p.slug}>
            <p className="param__nazwa">{p.tytul}</p>
            <LicznikParametru parametr={p} />
            <span className="param__ocena">
              <OcenaParametru parametr={p} />
            </span>
          </li>
        ))}
      </ul>

      {uwagi.map((u) => (
        <p className="wynik-uwaga" key={u}>
          {u}
        </p>
      ))}

      <p className="wynik-stopka">{stopkaOceny()}</p>
    </Card>
  );
}
